/**
 * useConversationalFlow — reusable linear step-machine for chat-style forms.
 *
 * Per PM broadcast 2026-04-17T20:58:53Z: extracted for Fork 4 (custom report
 * builder) + Smart Import portal + future flows. Built as a LINEAR steps[]
 * model — works for flows like "metric → filter → preview → save" or
 * "event → plan → confirm". Branching state machines (e.g. SmartImport's
 * column-confirm yes/no + override path) should keep a custom useReducer,
 * since steps[] can't express "on answer X go to step A, on answer Y skip
 * to step C". The SmartImport page was already committed as a reducer and
 * is NOT retrofitted here — this hook is for new linear flows.
 *
 * Contract: { steps, state, currentStep, advance, goBack, isComplete, reset }.
 * Framer Motion transitions + keyboard nav live in the consumer; the hook
 * stays logic-only to keep it testable without jsdom.
 */

import { useCallback, useMemo, useRef, useState } from 'react'

export interface Step<TId extends string = string, TValue = unknown> {
  id: TId
  label: string
  /** Optional validator. Return false to block `advance(value)`. */
  validate?: (value: TValue) => boolean
}

export interface ConversationalFlow<TId extends string, TValue> {
  readonly state: Readonly<Partial<Record<TId, TValue>>>
  readonly currentStep: Step<TId, TValue> | null
  readonly currentIndex: number
  readonly isComplete: boolean
  advance: (value: TValue) => boolean
  goBack: () => void
  reset: () => void
}

interface UseConversationalFlowArgs<TId extends string, TValue> {
  steps: ReadonlyArray<Step<TId, TValue>>
  initial?: Partial<Record<TId, TValue>>
}

// Pure transitions exported for testability without jsdom. The hook below
// wraps them with useState; everyone else (tests, Fork 4's report builder if
// it wants to run outside React) can call these directly.

export interface FlowSnapshot<TId extends string, TValue> {
  state: Partial<Record<TId, TValue>>
  currentIndex: number
}

export function initFlow<TId extends string, TValue>(
  initial?: Partial<Record<TId, TValue>>,
): FlowSnapshot<TId, TValue> {
  return { state: initial ?? {}, currentIndex: 0 }
}

export function advanceFlow<TId extends string, TValue>(
  snapshot: FlowSnapshot<TId, TValue>,
  steps: ReadonlyArray<Step<TId, TValue>>,
  value: TValue,
): { snapshot: FlowSnapshot<TId, TValue>; ok: boolean } {
  const step = steps[snapshot.currentIndex]
  if (!step) return { snapshot, ok: false }
  if (step.validate && !step.validate(value)) return { snapshot, ok: false }
  return {
    ok: true,
    snapshot: {
      state: { ...snapshot.state, [step.id]: value },
      currentIndex: Math.min(snapshot.currentIndex + 1, steps.length),
    },
  }
}

export function goBackFlow<TId extends string, TValue>(
  snapshot: FlowSnapshot<TId, TValue>,
): FlowSnapshot<TId, TValue> {
  return { ...snapshot, currentIndex: Math.max(0, snapshot.currentIndex - 1) }
}

export function useConversationalFlow<TId extends string, TValue = unknown>({
  steps,
  initial,
}: UseConversationalFlowArgs<TId, TValue>): ConversationalFlow<TId, TValue> {
  const [snapshot, setSnapshot] = useState<FlowSnapshot<TId, TValue>>(() =>
    initFlow<TId, TValue>(initial),
  )
  // QA #9 (2026-04-17, Fork 4 fix): the prior `advance` read `snapshot`
  // from the render closure + called `setSnapshot(result.snapshot)`. Two
  // synchronous `advance()` calls inside one React batch computed from the
  // same stale state → the first answer was silently overwritten by the
  // second. The ref below carries the latest snapshot across renders so
  // subsequent advances inside the same tick build on the up-to-date
  // state. We also mirror the ref update via a functional setState so
  // React's reconciliation still holds if it ever batches across renders.
  const snapshotRef = useRef<FlowSnapshot<TId, TValue>>(snapshot)
  snapshotRef.current = snapshot

  const currentStep = useMemo<Step<TId, TValue> | null>(
    () =>
      snapshot.currentIndex < steps.length
        ? steps[snapshot.currentIndex]
        : null,
    [steps, snapshot.currentIndex],
  )

  const advance = useCallback(
    (value: TValue): boolean => {
      const result = advanceFlow(snapshotRef.current, steps, value)
      if (result.ok) {
        snapshotRef.current = result.snapshot
        setSnapshot(result.snapshot)
      }
      return result.ok
    },
    [steps],
  )

  const goBack = useCallback(() => {
    setSnapshot((s) => {
      const next = goBackFlow(s)
      snapshotRef.current = next
      return next
    })
  }, [])

  const reset = useCallback(() => {
    const fresh = initFlow<TId, TValue>(initial)
    snapshotRef.current = fresh
    setSnapshot(fresh)
  }, [initial])

  return {
    state: snapshot.state,
    currentStep,
    currentIndex: snapshot.currentIndex,
    isComplete: snapshot.currentIndex >= steps.length,
    advance,
    goBack,
    reset,
  }
}
